import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Bar, Line } from 'react-chartjs-2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowTrendUp,
  faUserGraduate,
  faHandshake,
  faRotateRight,
  faWallet,
  faPen,
  faCheck,
  faPiggyBank,
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons';
import { Panel, Segmented, ChartBox, money } from './Dashui';
import { useChartTheme, baseOptions, barStyle } from './Chartkit';
import BalanceCard from './BalanceCard';
import './Dashboard.css';
import './Growth.css';

const API = process.env.REACT_APP_API_BASE_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('userData')}` } });
const GOAL_KEY = 'rm-growth-goal';
const RANGES = [
  ['6', '6M'],
  ['12', '12M'],
  ['24', '24M'],
];

const readGoal = () => {
  try {
    return Number(localStorage.getItem(GOAL_KEY)) || 100;
  } catch {
    return 100;
  }
};
const saveGoal = (n) => {
  try {
    localStorage.setItem(GOAL_KEY, String(n));
  } catch {
    /* private mode */
  }
};

const ymOf = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
const shiftYm = (ym, delta) => {
  const [y, m] = ym.split('-').map(Number);
  return ymOf(new Date(y, m - 1 + delta, 1));
};
const longMonth = (ym) => {
  const [y, m] = ym.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};

/* This month's money: expected teacher pay (so you keep it aside) and what is left */
const MonthMoney = () => {
  const thisYm = ymOf(new Date());
  const [ym, setYm] = useState(thisYm);
  const [f, setF] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    let alive = true;
    setF(null);
    setErr('');
    axios
      .get(`${API}/api/admin/growth/forecast?month=${ym}`, auth())
      .then((res) => alive && setF(res.data))
      .catch((e) => alive && setErr(e.response?.data?.error || 'We couldn’t work out this month.'));
    return () => {
      alive = false;
    };
  }, [ym]);

  const t = f?.teachers;
  const takenOver = f ? Math.round((f.costs.personal - Math.max(f.left, 0)) * 100) / 100 : 0;
  const safeToTake = f ? Math.round((f.left - f.costs.personal) * 100) / 100 : 0;
  const rows = t
    ? [
        ['Approved classes', t.counts.approved, t.approved],
        ['Submitted, waiting for your approval', t.counts.waiting, t.waiting],
        ['Still to come (in the schedule)', t.counts.toCome, t.toCome],
        ['Demos ($1 each)', t.counts.demos, t.demos],
        ['Enrollment bonuses', t.counts.bonuses, t.bonuses],
        ['Incentives, penalties, arrears', null, t.extras],
      ]
    : [];

  return (
    <Panel
      title={
        <>
          <FontAwesomeIcon icon={faPiggyBank} /> Money for {longMonth(ym)}
        </>
      }
      sub="Keep the teachers’ pay aside first. What is left after teachers and costs is what you can take."
      actions={
        <div className="rmgr-monthnav">
          <button type="button" onClick={() => setYm((v) => shiftYm(v, -1))} aria-label="Previous month">
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          <button type="button" onClick={() => setYm((v) => shiftYm(v, 1))} disabled={ym >= thisYm} aria-label="Next month">
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>
      }
    >
      {err && <p className="rmgr-warn">{err}</p>}
      {!f && !err && <div className="rmadd-chart rmadd-skel" />}
      {f && (
        <div className="rmgr-money">
          <div className="rmgr-aside">
            <span>Keep aside for teachers</span>
            <strong>{money(t.expected)}</strong>
            <ul>
              {rows.map(([label, n, amount]) => (
                <li key={label}>
                  <span>
                    {label}
                    {n != null && <i> × {n}</i>}
                  </span>
                  <b>{money(amount)}</b>
                </li>
              ))}
            </ul>
          </div>
          <div className="rmgr-left">
            <ul>
              <li>
                <span>Cash in this month (paid packages)</span>
                <b>{money(f.cashIn)}</b>
              </li>
              <li>
                <span>− Teachers (expected)</span>
                <b>{money(t.expected)}</b>
              </li>
              <li>
                <span>− Business costs{f.costs.business < f.costs.businessExpected ? ' (usual month)' : ''}</span>
                <b>{money(f.costs.businessExpected)}</b>
              </li>
              <li className="is-total">
                <span>Left for you this month</span>
                <b className={f.left < 0 ? 'is-bad' : 'is-good'}>{money(f.left)}</b>
              </li>
              <li>
                <span>Personal already taken (Expenses)</span>
                <b>{money(f.costs.personal)}</b>
              </li>
            </ul>
            <p className={`rmgr-verdict ${safeToTake < 0 ? 'is-bad' : 'is-good'}`}>
              {safeToTake >= 0
                ? `You can still take ${money(safeToTake)} this month.`
                : `You took ${money(takenOver)} more than this month left. Take less next month, or the teachers’ pay will come from next month’s cash.`}
            </p>
          </div>
        </div>
      )}
    </Panel>
  );
};

const short = (m) => {
  const [y, mo] = m.split('-').map(Number);
  return new Date(y, mo - 1, 1).toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
};
const pctText = (v) => (v == null ? '—' : `${v}%`);
const sum = (list, k) => list.reduce((t, x) => t + (Number(x[k]) || 0), 0);
// green / orange / red for a conversion %
const tone = (v, good, ok) => (v == null ? '' : v >= good ? 'is-good' : v >= ok ? 'is-ok' : 'is-bad');

/* Admin › Growth: the three numbers that decide how fast RoboMind grows —
   demo conversion, first renewal, active students — plus conversion per teacher. */
const Growth = () => {
  const { c } = useChartTheme();
  const [range, setRange] = useState('12');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [goal, setGoal] = useState(readGoal);
  const [goalEdit, setGoalEdit] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/api/admin/growth?months=${range}&teacherMonths=3`, auth());
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'We couldn’t load the growth numbers.');
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    load();
  }, [load]);

  const months = useMemo(() => data?.months || [], [data]);
  const last3 = months.slice(-3);
  const conv3 = (() => {
    const h = sum(last3, 'demosHappened');
    return h ? Math.round((1000 * sum(last3, 'enrolled')) / h) / 10 : null;
  })();
  const thisMonth = months[months.length - 1];
  // the real number of active students: classes still in their schedule (same as Students > Active)
  const active = data?.now?.activeStudents || 0;
  // students who already took a class this month (low at the start of a month)
  const tookThisMonth = thisMonth?.activeStudents || 0;
  const lastMonthActive = months.length > 1 ? months[months.length - 2].activeStudents : null;
  // a month's active students: the students who had classes that month; this month (not over yet) = the real
  // active number now (classes in their schedule), not only the few who already had a class
  const activeOf = (m, i) => (i === months.length - 1 && data?.now ? active : m.activeStudents);
  const goalPct = Math.min(100, Math.round((100 * active) / Math.max(goal, 1)));
  const notSet = sum(last3, 'reportNotSet');

  const labels = months.map((m) => short(m.month));
  const demoChart = {
    labels,
    datasets: [
      { label: 'Demos done', data: months.map((m) => m.demosHappened), ...barStyle(c.s1) },
      { label: 'Enrolled', data: months.map((m) => m.enrolled), ...barStyle(c.s3) },
    ],
  };
  const flowChart = {
    labels,
    datasets: [
      { label: 'New students', data: months.map((m) => m.newStudents), ...barStyle(c.s3) },
      { label: 'Renewals', data: months.map((m) => m.renewals), ...barStyle(c.s1) },
      { label: 'Stopped', data: months.map((m) => m.lost), ...barStyle(c.s2) },
    ],
  };
  const activeChart = {
    labels,
    datasets: [
      {
        label: 'Students with classes',
        data: months.map(activeOf),
        borderColor: c.s1,
        backgroundColor: c.s1,
        pointRadius: 3,
        tension: 0.3,
      },
    ],
  };
  const activeOptions = (() => {
    const o = baseOptions(c, { integer: true });
    o.scales.y.suggestedMax = goal;
    o.plugins.annotation = {
      annotations: {
        goal: {
          type: 'line',
          yMin: goal,
          yMax: goal,
          borderColor: c.s3,
          borderWidth: 2,
          borderDash: [6, 4],
          label: { display: true, content: `Goal ${goal}`, position: 'start', backgroundColor: c.s3, color: '#FFFFFF', font: { size: 11, weight: '700' } },
        },
      },
    };
    return o;
  })();

  const doneGoal = () => {
    const n = Math.max(1, Math.min(10000, parseInt(goalEdit, 10) || goal));
    setGoal(n);
    saveGoal(n);
    setGoalEdit(null);
  };

  return (
    <div className="rmadd rmgr">
      <div className="rmadd-inner">
        <header className="rmadd-head rmgr-head">
          <div>
            <span className="rmadd-eyebrow">Growth</span>
            <h1 className="rmadd-title">
              <FontAwesomeIcon icon={faArrowTrendUp} /> Road to {goal} students
            </h1>
          </div>
          <Segmented options={RANGES} value={range} onChange={setRange} label="Months shown" />
        </header>

        {error && (
          <ChartBox error={error} onRetry={load} />
        )}

        {!error && (
          <>
            <BalanceCard />
            <MonthMoney />

            {/* ---------- The numbers that matter ---------- */}
            <div className={`rmgr-kpis ${loading ? 'is-loading' : ''}`}>
              <div className="rmadd-kpi rmadd-kpi--accent">
                <span className="rmadd-kpi-label">
                  <FontAwesomeIcon icon={faUserGraduate} /> Active students
                </span>
                <strong className="rmadd-kpi-big">{loading ? '–' : active}</strong>
                {!loading && (
                  <p className="rmadd-kpi-note">
                    with classes in their schedule now · {tookThisMonth} already had a class this month
                    {lastMonthActive != null ? ` (last month ${lastMonthActive})` : ''}
                  </p>
                )}
                {!loading && data?.now?.finishedNotRenewed > 0 && (
                  <p className="rmadd-kpi-note">
                    <b className="rmgr-warn">+{data.now.finishedNotRenewed}</b> finished their package in the last 30 days, not renewed yet
                  </p>
                )}
                <p className="rmadd-kpi-note">
                  {goalEdit == null ? (
                    <>
                      {goalPct}% of the goal of {goal}{' '}
                      <button type="button" className="rmgr-goal-btn" onClick={() => setGoalEdit(String(goal))} aria-label="Change the goal">
                        <FontAwesomeIcon icon={faPen} />
                      </button>
                    </>
                  ) : (
                    <span className="rmgr-goal-edit">
                      Goal
                      <input
                        type="number"
                        min="1"
                        value={goalEdit}
                        onChange={(e) => setGoalEdit(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && doneGoal()}
                        autoFocus
                        aria-label="Student goal"
                      />
                      <button type="button" className="rmgr-goal-btn" onClick={doneGoal} aria-label="Save the goal">
                        <FontAwesomeIcon icon={faCheck} />
                      </button>
                    </span>
                  )}
                </p>
                <span className="rmadd-kpi-bar" aria-hidden="true">
                  <span style={{ width: `${loading ? 0 : goalPct}%` }} />
                </span>
              </div>

              <div className="rmadd-kpi">
                <span className="rmadd-kpi-label">
                  <FontAwesomeIcon icon={faHandshake} /> Demo → enrolled
                </span>
                <strong className={`rmadd-kpi-big ${tone(conv3, 40, 30)}`}>{loading ? '–' : pctText(conv3)}</strong>
                <p className="rmadd-kpi-note">
                  Last 3 months · this month {loading ? '–' : pctText(thisMonth?.conversion)}
                  {!loading && notSet > 0 && <b className="rmgr-warn"> · {notSet} demo reports not set</b>}
                </p>
              </div>

              <div className="rmadd-kpi">
                <span className="rmadd-kpi-label">
                  <FontAwesomeIcon icon={faRotateRight} /> First renewal
                </span>
                <strong className={`rmadd-kpi-big ${tone(data?.firstRenewal?.rate, 75, 60)}`}>{loading ? '–' : pctText(data?.firstRenewal?.rate)}</strong>
                <p className="rmadd-kpi-note">
                  {loading ? '–' : `${data?.firstRenewal?.renewed || 0} of ${data?.firstRenewal?.students || 0} new students bought a 2nd package`}
                </p>
              </div>

              <div className="rmadd-kpi">
                <span className="rmadd-kpi-label">
                  <FontAwesomeIcon icon={faWallet} /> Not paid yet
                </span>
                <strong className={`rmadd-kpi-big ${data?.now?.unpaid?.amount > 0 ? 'is-bad' : ''}`}>{loading ? '–' : money(data?.now?.unpaid?.amount)}</strong>
                <p className="rmadd-kpi-note">{loading ? '–' : `${data?.now?.unpaid?.count || 0} payments waiting`}</p>
              </div>
            </div>

            {/* ---------- Charts ---------- */}
            <div className="rmadd-grid-2">
              <Panel title="Active students" sub="Students who had classes each month (this month: active now, with classes in their schedule), against your goal">
                <ChartBox loading={loading} empty={!months.length}>
                  <Line data={activeChart} options={activeOptions} />
                </ChartBox>
              </Panel>
              <Panel title="Demos and enrollments" sub="Demos that happened and how many enrolled">
                <ChartBox loading={loading} empty={!months.length}>
                  <Bar data={demoChart} options={baseOptions(c, { integer: true })} />
                </ChartBox>
              </Panel>
            </div>

            <Panel title="Students in and out" sub="New students and renewals (paid) against students who stopped">
              <ChartBox loading={loading} empty={!months.length}>
                <Bar data={flowChart} options={baseOptions(c, { integer: true })} />
              </ChartBox>
            </Panel>

            {/* ---------- Teachers ---------- */}
            <Panel title="Teachers · last 3 months" sub="Who keeps students and who closes demos. Give demos to the best closers.">
              {loading ? (
                <div className="rmadd-chart rmadd-skel" />
              ) : (
                <div className="rmgr-table-wrap">
                  <table className="rmgr-table">
                    <thead>
                      <tr>
                        <th>Teacher</th>
                        <th>Classes / week</th>
                        <th>Students</th>
                        <th>Half / no pay</th>
                        <th>Demos</th>
                        <th>Enrolled</th>
                        <th>Conversion</th>
                        <th>Reports not set</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data?.teachers || []).map((t) => (
                        <tr key={t.id || t.name}>
                          <th scope="row">{t.name}</th>
                          <td>{t.perWeek}</td>
                          <td>{t.students}</td>
                          <td className={t.halfOrNoPay >= 5 ? 'is-bad' : ''}>{t.halfOrNoPay}</td>
                          <td>{t.demos}</td>
                          <td>{t.enrolled}</td>
                          <td className={`rmgr-conv ${tone(t.conversion, 40, 25)}`}>{pctText(t.conversion)}</td>
                          <td className={t.reportNotSet > 0 ? 'is-ok' : ''}>{t.reportNotSet}</td>
                        </tr>
                      ))}
                      {!(data?.teachers || []).length && (
                        <tr>
                          <td colSpan={8} className="rmgr-none">
                            No classes or demos in the last 3 months.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </Panel>

            {/* ---------- Month by month ---------- */}
            <Panel title="Month by month" sub="First renewal = students whose first package was paid that month and who bought a second one">
              {loading ? (
                <div className="rmadd-chart rmadd-skel" />
              ) : (
                <div className="rmgr-table-wrap">
                  <table className="rmgr-table">
                    <thead>
                      <tr>
                        <th>Month</th>
                        <th>Active</th>
                        <th>Classes</th>
                        <th>Demos</th>
                        <th>Conversion</th>
                        <th>New</th>
                        <th>Renewals</th>
                        <th>Stopped</th>
                        <th>First renewal</th>
                        <th>Cash in</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...months].reverse().map((m) => {
                        const co = (data?.cohorts || []).find((x) => x.month === m.month) || {};
                        return (
                          <tr key={m.month}>
                            <th scope="row">{short(m.month)}</th>
                            <td>{activeOf(m, months.indexOf(m))}</td>
                            <td>{m.classes}</td>
                            <td>{m.demosHappened}</td>
                            <td className={`rmgr-conv ${tone(m.conversion, 40, 30)}`}>{pctText(m.conversion)}</td>
                            <td>{m.newStudents}</td>
                            <td>{m.renewals}</td>
                            <td className={m.lost >= 5 ? 'is-bad' : ''}>{m.lost}</td>
                            <td>{co.firstPayments ? `${co.renewed}/${co.firstPayments}` : '—'}</td>
                            <td>{money(m.cashIn)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </Panel>
          </>
        )}
      </div>
    </div>
  );
};

export default Growth;
